import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import { FormRef, FormRefType } from '@lib/components/ReactHookForm/types';
import { LocalWidget } from 'routes/Home/hooks/useWidgetEditor';
import WidgetModalFormActions from './WidgetModalFormActions';
import useWidgetModalFormActions from './utils/useWidgetModalFormActions';

export interface WidgetModalFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
  onSubmit: (values: Values) => void;
  formRef?: FormRefType;
  isEditMode?: boolean;
  editingWidget?: LocalWidget | null;
  loading?: boolean;
}

export default function WidgetModalForm(props: WidgetModalFormProps) {
  const {
    isOpen,
    onCancel,
    onClose,
    onSubmit,
    formRef,
    isEditMode = false,
    editingWidget,
    loading = false,
  } = props;
  const { t } = useTranslation();

  const { fields, defaultFormValues, internalFormRef } =
    useWidgetModalFormActions({
      isEditMode,
      editingWidget,
    });

  // Sync internal ref to external formRef if provided
  useEffect(() => {
    if (formRef && internalFormRef.current) {
      // eslint-disable-next-line no-param-reassign
      (formRef as { current: FormRef | undefined }).current =
        internalFormRef.current;
    }
  });

  const actionsComponent: (
    actionsComponentProps: ActionsProps,
  ) => React.ReactElement = (actionsComponentProps) => (
    <WidgetModalFormActions
      {...actionsComponentProps}
      onCancel={onCancel}
      isEditMode={isEditMode}
    />
  );

  const actionsComponentProps = { onCancel, isEditMode };

  const modalCaption = isEditMode ? t('edit-widget') : t('add-widget');

  return (
    <Modal isOpen={isOpen} onClose={onClose} caption={modalCaption}>
      <Form
        actions={actionsComponent}
        actionsComponentProps={actionsComponentProps}
        defaultValues={defaultFormValues}
        fields={fields}
        formId={isEditMode ? 'edit-widget-modal-form' : 'add-widget-modal-form'}
        formRef={internalFormRef}
        loading={loading}
        onSubmit={onSubmit}
      />
    </Modal>
  );
}
